import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import PopulateFrom from './PopulateFrom';
import type { TransportType } from '../types';

interface ConfigFieldProps {
  label: string;
  current: TransportType;
  /** Called with the chosen source mode when the inline 📥 button is used.
   * Optional — some fields (e.g. the band-specific legal intro) are edited
   * and saved independently and have no cross-transport populate source. */
  onPopulateFrom?: (mode: TransportType) => void;
  children: ReactNode;
  /** Render the label as a block (textarea/select) vs inline (input). */
  labelBlock?: boolean;
  /** Rider Layout field id (e.g. 'sound.foh') — enables ?focus= deep-linking
   * from the Layout Editor so the user lands directly on this field. */
  fieldId?: string;
  /** Pre-filled example text shown in the field's placeholder. When present
   * together with onUseExample, ConfigField renders a "📝 Use example" button
   * that materialises this text as the field's real value (a starting point
   * the user can then edit). Optional — only for fields with an "e.g." example
   * placeholder, never for plain labels/shortcuts. */
  exampleText?: string;
  /** Called with the example text when the "📝 Use example" button is clicked.
   * The parent writes the text into the field via its onChange. */
  onUseExample?: (text: string) => void;
  /**
   * Freeze the field's own actions (lot A2-CAP): the "Use example" button and
   * the inline "Populate from" control both WRITE through the parent, so a
   * caller that holds no write capability disables them here. Defaults to
   * false — a normal editor is unaffected.
   */
  disabled?: boolean;
}

/**
 * Form field wrapper used across CONFIGURATION pages: renders the label with
  * an inline "📝 Use example" button (when the field has an example placeholder)
  * and an inline "📥 Populate from" button on the right, then the control.
  *
  * P3c: the root also carries `.rc-form-row` so every config field follows the
  * shared Roadcase row rhythm (hairline + density) — the config pages STRETCH
  * it into a column (label over control) in App.css. Purely presentational.
  */
export default function ConfigField({
  label,
  current,
  onPopulateFrom,
  children,
  labelBlock = false,
  fieldId,
  exampleText,
  onUseExample,
  disabled = false,
}: ConfigFieldProps) {
  const { t } = useTranslation();
  const canUseExample = exampleText !== undefined && !!onUseExample;
  return (
    <div className="form-group rc-form-row" {...(fieldId ? { 'data-field-id': fieldId } : {})}>
      <label className={labelBlock ? 'config-field-label-block' : 'config-field-label'}>
        <span>{label}</span>
        <span className="config-field-actions">
          {canUseExample && (
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={() => onUseExample?.(exampleText as string)}
              title={t('config.useExampleTitle')}
              disabled={disabled}
            >
              {t('config.useExample')}
            </button>
          )}
          {onPopulateFrom && <PopulateFrom current={current} compact onSelect={onPopulateFrom} disabled={disabled} />}
        </span>
      </label>
      {children}
    </div>
  );
}
